import { LineChart, type LineChartProps } from "@mantine/charts";
import { Button, Group, Stack } from "@mantine/core";
import * as Sentry from "@sentry/browser";
import * as _ from "lodash-es";
import { useMemo, useState } from "react";
import { numericFormatter } from "react-number-format";
import { ReferenceArea, type TooltipPayload } from "recharts";
import { THEME_VARS } from "../../../utils/constants";
import { getDefaultNumericFormatterProps } from "../../../utils/getDefaultNumericFormatterProps";
import { MantineChartLegend } from "../common/MantineChartLegend";
import { downsamplePreservingExtremes } from "../utils/downsamplePreservingExtremes";
import { getColoredSeries } from "../utils/getColoredSeries";
import "./MantineLineChart.scss";
import { MantineLineChartTooltip } from "./MantineLineChartTooltip";

type ZoomState = {
  refAreaLeft?: string | number;
  refAreaRight?: string | number;
  left: string | number;
  right: string | number;
};

const MAX_LINE_CHART_POINTS = 30000;

const initialZoomState: ZoomState = {
  refAreaLeft: undefined,
  refAreaRight: undefined,
  left: "dataMin",
  right: "dataMax"
};

interface MantineLineChartProps extends LineChartProps {
  zoomable?: boolean;
  legendPosition?: "top" | "right";
}

function MantineLineChart({
  data,
  dataKey,
  h = 450,
  lineChartProps,
  lineProps,
  series,
  withLegend,
  xAxisProps,
  yAxisProps,
  zoomable = true,
  legendPosition = "right",
  ...props
}: MantineLineChartProps) {
  const [zoomState, setZoomState] = useState<ZoomState>(initialZoomState);
  const [activeSeries, setActiveSeries] = useState<Array<string>>([]);

  const xAxisTypeIsNumber = xAxisProps?.type === "number";
  const reallyZoomable = zoomable && xAxisTypeIsNumber;

  const mutableData = useMemo(
    () => (data ?? []).map((dataEntry) => ({ ...dataEntry })),
    [data]
  );

  const coloredSeries = getColoredSeries(series);

  function handleLegendToggle(seriesId: string) {
    setActiveSeries((prev) =>
      prev.includes(seriesId)
        ? prev.filter((id) => id !== seriesId)
        : [...prev, seriesId]
    );
  }

  function zoomIn() {
    if (
      !zoomState.refAreaLeft ||
      !zoomState.refAreaRight ||
      zoomState.refAreaLeft === zoomState.refAreaRight ||
      zoomState.refAreaRight === ""
    ) {
      setZoomState({ ...zoomState, refAreaLeft: "", refAreaRight: "" });
      return;
    }

    // xAxis domain
    if (
      zoomState.refAreaLeft &&
      zoomState.refAreaRight &&
      zoomState.refAreaLeft > zoomState.refAreaRight
    )
      [zoomState.refAreaLeft, zoomState.refAreaRight] = [
        zoomState.refAreaRight,
        zoomState.refAreaLeft
      ];

    setZoomState({
      ...zoomState,
      refAreaLeft: undefined,
      refAreaRight: undefined,
      left: zoomState.refAreaLeft,
      right: zoomState.refAreaRight
    });
  }

  function zoomOut() {
    setZoomState(initialZoomState);
  }

  // This is for discrete x-axis values - would be unnecessary if Mantine supported this
  // (offered tickGap instead of only minTickGap)
  const isZoomed =
    zoomState.left !== "dataMin" || zoomState.right !== "dataMax";

  const optimizedData = useMemo(
    () =>
      isZoomed
        ? mutableData
        : downsamplePreservingExtremes(
            mutableData,
            series.map(({ name }) => name),
            MAX_LINE_CHART_POINTS
          ),
    [isZoomed, mutableData, series]
  );

  const allManualXAxisValues = xAxisTypeIsNumber
    ? optimizedData
        .map((dataEntry) => dataEntry[dataKey])
        .filter((x) =>
          isZoomed ? x >= zoomState.left && x <= zoomState.right : true
        )
    : [];
  const tickGap = Math.floor(
    allManualXAxisValues.length / (xAxisProps?.tickCount || 5)
  );
  let densedManualXAxisValues = xAxisTypeIsNumber
    ? _.range(xAxisProps.tickCount || 5)
        .map((i) => allManualXAxisValues[i * tickGap])
        .concat(allManualXAxisValues.slice(-1))
    : [];
  if (densedManualXAxisValues.length > allManualXAxisValues.length) {
    densedManualXAxisValues = allManualXAxisValues;
  }

  return (
    <Group
      align="start"
      className="MantineLineChart"
      gap={0}
      style={{
        flexDirection: legendPosition === "top" ? "column-reverse" : "row"
      }}
    >
      <Stack
        mt={withLegend && legendPosition === "top" ? "md" : 0}
        w={withLegend && legendPosition === "right" ? "80%" : "100%"}
      >
        {reallyZoomable && (
          <Group>
            <Button
              disabled={
                zoomState.left === "dataMin" && zoomState.right === "dataMax"
              }
              size="compact-xs"
              variant="default"
              onClick={zoomOut}
            >
              Zoom zurücksetzen
            </Button>
          </Group>
        )}
        <LineChart
          connectNulls={false}
          curveType="stepAfter"
          data={optimizedData}
          dataKey={dataKey}
          h={h}
          series={coloredSeries}
          withDots={false}
          {...props}
          lineChartProps={
            reallyZoomable
              ? {
                  onMouseDown: (nextState, e) => {
                    /* For _some_ mysterious reason, we had an reoccuring sentry issue where nextState was null.
                       Therefore, please don't remove the ? and the fallback. */
                    if (!nextState) {
                      Sentry.captureEvent({
                        message:
                          "MantineLineChart onMouseDown nextState was null",
                        logentry: { params: [{ nextState, e }] }
                      });
                    }
                    setZoomState({
                      ...zoomState,
                      refAreaLeft:
                        nextState?.activeLabel || zoomState.refAreaLeft,
                      refAreaRight:
                        nextState?.activeLabel || zoomState.refAreaRight
                    });
                  },
                  onMouseMove: (nextState) =>
                    zoomState.refAreaLeft &&
                    setZoomState({
                      ...zoomState,
                      refAreaRight:
                        nextState?.activeLabel || zoomState.refAreaRight
                    }),
                  onMouseUp: zoomIn,
                  ...lineChartProps
                }
              : lineChartProps
          }
          lineProps={(series) => ({
            ...lineProps,
            hide: activeSeries.includes(series.name)
          })}
          referenceLines={[
            { x: zoomState.refAreaLeft },
            { x: zoomState.refAreaRight },
            { y: 0, stroke: THEME_VARS.neutral[6] }
          ]}
          tooltipProps={{
            content: ({
              label,
              payload
            }: {
              label?: string | number;
              payload: TooltipPayload;
            }) => {
              const title =
                label && xAxisProps?.tickFormatter
                  ? xAxisProps.tickFormatter(label, 0)
                  : label;
              return (
                <MantineLineChartTooltip
                  payload={payload}
                  seriesLabels={coloredSeries.reduce(
                    (result, singleSeries) => ({
                      ...result,
                      [singleSeries.name]:
                        singleSeries.label || singleSeries.name
                    }),
                    {}
                  )}
                  title={String(title)}
                />
              );
            }
          }}
          withLegend={false}
          xAxisProps={{
            tickFormatter: (value) =>
              xAxisProps?.type === "number"
                ? numericFormatter(
                    value.toString(),
                    getDefaultNumericFormatterProps()
                  )
                : value,
            ...xAxisProps,
            allowDataOverflow: true,
            domain: [zoomState.left, zoomState.right],
            ticks: xAxisTypeIsNumber ? densedManualXAxisValues : undefined
          }}
          yAxisProps={{
            tickFormatter: (value) =>
              numericFormatter(
                value.toString(),
                getDefaultNumericFormatterProps()
              ),
            ...yAxisProps,
            allowDataOverflow: true,
            domain: ([dataMin, dataMax]) => [
              Math.min(0, dataMin),
              Math.max(0, dataMax)
            ]
          }}
        >
          <ReferenceArea
            x1={zoomState.refAreaLeft}
            x2={zoomState.refAreaRight}
            yAxisId={"left"}
          />
        </LineChart>
      </Stack>
      {withLegend && series.length > 0 && (
        <MantineChartLegend
          direction={legendPosition === "top" ? "row" : "column"}
          inactiveSeries={activeSeries}
          series={coloredSeries}
          onClick={handleLegendToggle}
        />
      )}
    </Group>
  );
}

export { MantineLineChart, MantineLineChartProps };
